import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationService } from '@services/notification/notification.service';
import { environment } from '../../../environments/environment';
import * as Sentry from "@sentry/angular-ivy";

@Injectable()
export class GlobalErrorHandler extends ErrorHandler {

    constructor(
        private injector: Injector
    ) {
        super();
    }

    handleError(error: Error) {
        const chunkFailedMessage = /Loading chunk [\d]+ failed/;
        if (chunkFailedMessage.test(error.message)) {
            window.location.reload();
        }
        else {
            const router = this.injector.get(Router);
            const zone = this.injector.get(NgZone);
            const notification = this.injector.get(NotificationService);

            const { normalizedError, tags, extras } = this.processError(error);

            zone.run(() => {
                super.handleError(error);

                Sentry.withScope((scope) => {
                    scope.setTags(tags);
                    scope.setExtras(extras);

                    Sentry.captureException(normalizedError);
                });

                if (environment.production) {
                    notification.showClientError();
                    router.navigate(['/main']);
                }
            });
        }
    }

    private processError(error: any): {
        normalizedError: Error;
        tags: Record<string, string>;
        extras: Record<string, any>;
    } {
        let tags: Record<string, string> = {};
        let extras: Record<string, any> = {};
        let normalizedError: Error;

        if (error instanceof HttpErrorResponse) {
            // Angular HTTP error
            tags = {
                origin: 'http',
                status: error.status.toString(),
                url: error.url ?? 'unknown',
            };

            extras = {
                error: error.error,
                headers: error.headers,
                message: error.message,
                statusText: error.statusText,
            };

            normalizedError = new Error(`HTTP ${error.status} - ${error.message}`);
            (normalizedError as any).originalError = error;
        } else if (error instanceof Error) {
            // Regular JS error
            tags = { origin: 'js' };
            normalizedError = error;
        } else {
            // Unknown / plain object
            tags = { origin: 'unknown' };
            normalizedError = new Error('Non-Error exception captured');
            extras = { original: error };
            (normalizedError as any).originalError = error;
        }

        return { normalizedError, tags, extras };
    }
}
